import React, { useState, useRef } from "react";
import { useField, useFormikContext } from "formik";
import { FormField, FileInput, SystemIcon } from "@justworkshr/milo-components";
import {
  useAddDocumentMutation,
  useDeleteDocumentMutation,
} from "types/generated/operations";
import styles from "./DocumentUpload.module.css";
import { errorMessageMap, getWWRedirectUrl } from "utils/helper";
import { WCAppType } from "types";
import { trackFormError } from "utils/analytics/trackEvent";
import { useOktaAuth } from "@okta/okta-react";

interface IDocumentUploadProps {
  name: string;
  label: string;
  message?: string;
  onChange?: () => void;
  placeholder?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  required?: boolean;
  application?: WCAppType;
  fieldRefs?: React.MutableRefObject<{
    [key: string]: HTMLInputElement | null;
  }>;
}

const ACCEPTED_FILE_TYPES = ".pdf";

const DocumentUpload = ({ ...props }: IDocumentUploadProps): JSX.Element => {
  const {
    name,
    label,
    required = true,
    message,
    application,
    fieldRefs,
  } = props;

  const fileInputRef = useRef<HTMLButtonElement>(null);
  const { authState } = useOktaAuth();
  const [currentDocument, setCurrentDocument] = useState(
    application?.documents?.find((item) => item.applicationField === name)
  );
  const [documentError, setDocumentError] = useState("");

  const appID = application?.uuid;
  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext();

  const [addDocument] = useAddDocumentMutation();

  const [deleteDocument] = useDeleteDocumentMutation();

  const handleOnFileChange = async (file?: File | File[] | undefined) => {
    if (!appID) {
      setError(Error("no app ID while uploading document"), "Try Again");
      return;
    }
    try {
      // we only support single file upload
      let selectedFile: File | undefined = undefined;
      if (file && Array.isArray(file)) {
        if (file.length > 0) {
          selectedFile = file[0];
        } else if (file instanceof File) {
          selectedFile = file;
        }
      }
      if (!selectedFile) {
        await handleDeleteFile();
        return;
      }

      const url = getWWRedirectUrl("/document-upload");
      const formData = new FormData();
      formData.append("file", selectedFile);

      // Add Document on s3
      const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: "Bearer " + authState?.accessToken?.accessToken,
        },
        body: formData,
      });
      const result = await response.json();

      if (result?.error) {
        throw Error(result?.error);
      }
      // Add document on graph
      const addDocumentResponse = await addDocument({
        variables: {
          applicationId: appID,
          applicationField: field.name,
          fileSize: result?.file_size,
          path: result?.key,
        },
      });
      const uuid = addDocumentResponse.data?.createDocument?.uuid;
      setFieldValue(field.name, uuid);
      const newDocument = {
        ...currentDocument,
        accessLink: "",
        applicationField: field.name,
        fileName: "",
        uuid: uuid || "",
      };
      setCurrentDocument(newDocument);
      setDocumentError("");
    } catch (error: any) {
      setError(error, error?.message);
      return;
    }
  };

  const handleDeleteFile = async () => {
    if (!currentDocument?.uuid) return;
    try {
      await deleteDocument({
        variables: {
          uuid: currentDocument.uuid,
        },
      });
      setFieldValue(field.name, "");
      const newDocument = {
        ...currentDocument,
        accessLink: "",
        applicationField: field.name,
        fileName: "",
        uuid: "",
      };
      setCurrentDocument(newDocument);
      setDocumentError("");
    } catch (error: any) {
      setError(error, error?.message);
      return;
    }
  };

  const setError = (error: Error, errorMessage: string) => {
    setFieldValue(field.name, "");
    fileInputRef.current?.click();
    const mappedError = errorMessageMap[errorMessage] || "Try Again";
    setDocumentError(mappedError);
    trackFormError(error, errorMessage);
  };

  const getErrorMessage = () => {
    if (documentError) {
      return documentError;
    }
    return meta.touched ? meta.error : "";
  };

  return (
    <div
      className={styles.container}
      ref={(el) => {
        if (fieldRefs?.current) {
          fieldRefs.current[name] = el as HTMLInputElement | null;
        }
      }}
    >
      <FormField
        label={label}
        error={getErrorMessage()}
        required={required}
        message={message}
        name={name}
      >
        <FileInput
          id={name}
          accept={ACCEPTED_FILE_TYPES}
          onFileChange={handleOnFileChange}
          disabled={currentDocument?.accessLink ? true : false}
          ref={fileInputRef}
        />
      </FormField>
      {currentDocument?.accessLink && (
        <div className={styles.preview}>
          <span>
            <a
              href={currentDocument?.accessLink}
              target="_blank"
              rel="noreferrer"
              className="milo--link"
            >
              {currentDocument.fileName}
            </a>
          </span>
          <SystemIcon
            className={styles.trashIcon}
            iconName="trash"
            color="brand"
            size="small"
            onClick={handleDeleteFile}
          />
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
